import { leaveTypeIdSchema } from '@lms/shared-types'
import type { LeaveTypeId } from '@lms/shared-types'

export interface LeaveTypeStyle {
  /** Soft tinted fill — used as bar background. */
  bg: string
  /** Border color at ~30% alpha of the base hue. */
  line: string
  /** Text color used on top of `bg`. Maintains >= 4.5:1 contrast (WCAG AA). */
  ink: string
  /** Saturated brand hue (Tailwind-500) — legend dots, number accents,
   *  and saturated bar fills. Single source of truth for the vivid palette
   *  shared app-wide (home tiles, calendars, chips, tabs, roster). */
  swatch: string
  /** Human-readable label, e.g. "Annual". */
  label: string
  /** Short label for compact bars, e.g. "Ann". */
  short: string
  /** Material Symbols icon name. */
  icon: string
}

/**
 * Canonical ordered list of leave-type IDs. Single source of truth derived
 * from the Zod schema — used by the toolbar dropdown, the page filter
 * defaults, and any UI that needs to enumerate all valid leave types.
 */
export const ALL_LEAVE_TYPES: ReadonlyArray<LeaveTypeId> = leaveTypeIdSchema.options

// Vivid per-type hues (Tailwind-500) — the punchy palette the redesigned
// "My Leave" tiles use. Promoted here to be the single app-wide source so
// every calendar, chip, tab, and roster reads the same bright color. `bg`,
// `line`, and `ink` are derived from each `swatch` via color-mix so a type's
// pale fill / border / text stay in the same hue family as its dot.
const SWATCH: Record<LeaveTypeId, string> = {
  annual: '#3b82f6',
  medical: '#ef4444',
  hospitalization: '#f97316',
  birthday: '#ec4899',
  compassionate: '#6366f1',
  marriage: '#14b8a6',
  // Retired — shares the marriage hue so historical rows read in the same family.
  marriage_family_care: '#14b8a6',
  maternity: '#db2777',
  paternity: '#8b5cf6',
  unpaid: '#64748b',
  replacement: '#f59e0b',
}

/** Soft translucent tint of a swatch — the pale bar/chip fill. */
function tintBg(hex: string): string {
  return `color-mix(in srgb, ${hex} 14%, transparent)`
}
/** Mid-strength translucent border derived from a swatch. */
function tintLine(hex: string): string {
  return `color-mix(in srgb, ${hex} 34%, transparent)`
}
/** Dark, saturated text tone of a swatch — kept dark for AA contrast on tintBg. */
function tintInk(hex: string): string {
  return `color-mix(in srgb, ${hex} 45%, #000)`
}

export const LEAVE_TYPE_STYLE: Record<LeaveTypeId, LeaveTypeStyle> = {
  annual: {
    bg: tintBg(SWATCH.annual),
    line: tintLine(SWATCH.annual),
    ink: tintInk(SWATCH.annual),
    swatch: SWATCH.annual,
    label: 'Annual',
    short: 'Ann',
    icon: 'event_available',
  },
  medical: {
    bg: tintBg(SWATCH.medical),
    line: tintLine(SWATCH.medical),
    ink: tintInk(SWATCH.medical),
    swatch: SWATCH.medical,
    label: 'Sick',
    short: 'Med',
    // Pill bottle — universally medical, no religious symbolism. Material
    // Symbols' default `medical_services` is a cross which reads as
    // Christian in Malaysia context.
    icon: 'medication',
  },
  hospitalization: {
    bg: tintBg(SWATCH.hospitalization),
    line: tintLine(SWATCH.hospitalization),
    ink: tintInk(SWATCH.hospitalization),
    swatch: SWATCH.hospitalization,
    label: 'Hospitalization',
    short: 'Hosp',
    // Heart-rate monitor line — unambiguously clinical without the cross
    // glyph used by `local_hospital`.
    icon: 'monitor_heart',
  },
  birthday: {
    bg: tintBg(SWATCH.birthday),
    line: tintLine(SWATCH.birthday),
    ink: tintInk(SWATCH.birthday),
    swatch: SWATCH.birthday,
    label: 'Birthday',
    short: 'Bday',
    icon: 'cake',
  },
  compassionate: {
    bg: tintBg(SWATCH.compassionate),
    line: tintLine(SWATCH.compassionate),
    ink: tintInk(SWATCH.compassionate),
    swatch: SWATCH.compassionate,
    label: 'Compassionate',
    short: 'Comp',
    icon: 'volunteer_activism',
  },
  marriage: {
    bg: tintBg(SWATCH.marriage),
    line: tintLine(SWATCH.marriage),
    ink: tintInk(SWATCH.marriage),
    swatch: SWATCH.marriage,
    label: 'Marriage',
    short: 'Marr',
    icon: 'favorite',
  },
  // Retired combined quota — history only. Label spells out the old name so
  // past records stay legible; never shown as a live tile or picker option.
  marriage_family_care: {
    bg: tintBg(SWATCH.marriage_family_care),
    line: tintLine(SWATCH.marriage_family_care),
    ink: tintInk(SWATCH.marriage_family_care),
    swatch: SWATCH.marriage_family_care,
    label: 'Marriage & Family Care',
    short: 'M&FC',
    icon: 'favorite',
  },
  maternity: {
    bg: tintBg(SWATCH.maternity),
    line: tintLine(SWATCH.maternity),
    ink: tintInk(SWATCH.maternity),
    swatch: SWATCH.maternity,
    label: 'Maternity',
    short: 'Mat',
    icon: 'child_care',
  },
  paternity: {
    bg: tintBg(SWATCH.paternity),
    line: tintLine(SWATCH.paternity),
    ink: tintInk(SWATCH.paternity),
    swatch: SWATCH.paternity,
    label: 'Paternity',
    short: 'Pat',
    icon: 'family_restroom',
  },
  unpaid: {
    bg: tintBg(SWATCH.unpaid),
    line: tintLine(SWATCH.unpaid),
    ink: tintInk(SWATCH.unpaid),
    swatch: SWATCH.unpaid,
    label: 'Unpaid',
    short: 'Unp',
    icon: 'pause_circle',
  },
  replacement: {
    bg: tintBg(SWATCH.replacement),
    line: tintLine(SWATCH.replacement),
    ink: tintInk(SWATCH.replacement),
    swatch: SWATCH.replacement,
    label: 'Replacement',
    short: 'Rep',
    icon: 'swap_horiz',
  },
}

/** Privacy sentinel: the API returns this when the viewer may not see the
 *  leave type. Mirrors `'hidden'` in the calendarLeaveSchema union. */
export const HIDDEN_LEAVE_TYPE = 'hidden' as const

/** A calendar leave type as seen on the wire — a real type or the sentinel. */
export type CalendarLeaveType = LeaveTypeId | typeof HIDDEN_LEAVE_TYPE

/** Neutral grey style for leaves whose type is hidden from the viewer. */
const HIDDEN_LEAVE_STYLE: LeaveTypeStyle = {
  bg: 'oklch(0.62 0 0 / 0.12)',
  line: 'oklch(0.62 0 0 / 0.28)',
  ink: 'oklch(0.32 0 0)',
  swatch: 'oklch(0.62 0 0)',
  label: 'Leave',
  short: 'Leave',
  icon: 'event_busy',
}

/** Resolve a bar style, tolerating the `'hidden'` privacy sentinel. */
export function leaveTypeStyle(type: CalendarLeaveType): LeaveTypeStyle {
  return type === HIDDEN_LEAVE_TYPE ? HIDDEN_LEAVE_STYLE : LEAVE_TYPE_STYLE[type]
}
